import React, { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

const GENERAL_TYPES = [
  { value: "GENERAL", label: "General inquiry" },
  { value: "INFORMATION", label: "Request more information" },
  { value: "PRICE_PAYMENT", label: "Price & payment terms" },
];

/** mode: "inquiry" | "viewing" | "availability" */
export function InquiryDialog({ open, onOpenChange, listingId, mode, maxGuests, endpoint, heading, subtitle }) {
  const { user } = useAuth();
  const isConsumer = user && (user.account_type || "PROFESSIONAL") === "CONSUMER";
  const authed = !!user;
  const viewing = mode === "viewing";
  const availability = mode === "availability";

  const [type, setType] = useState("GENERAL");
  const [fullName, setFullName] = useState("");
  const [email, setEmail] = useState("");
  const [mobile, setMobile] = useState("");
  const [preferred, setPreferred] = useState("");
  const [message, setMessage] = useState("");
  const [date, setDate] = useState("");
  const [time, setTime] = useState("");
  const [checkIn, setCheckIn] = useState("");
  const [checkOut, setCheckOut] = useState("");
  const [guests, setGuests] = useState("1");
  const [busy, setBusy] = useState(false);

  const reset = () => {
    setType("GENERAL"); setFullName(""); setEmail(""); setMobile(""); setPreferred(""); setMessage(""); setDate(""); setTime("");
    setCheckIn(""); setCheckOut(""); setGuests("1");
  };

  useEffect(() => { if (open) reset(); }, [open, mode]);

  const submit = async () => {
    const payload = {
      inquiry_type: availability ? "AVAILABILITY_REQUEST" : viewing ? "VIEWING_REQUEST" : type,
      message: message.trim(),
    };
    if (viewing) { payload.preferred_date = date; payload.preferred_time = time; }
    if (availability) { payload.check_in = checkIn; payload.check_out = checkOut; payload.guests = parseInt(guests || 0); }
    if (!authed) { payload.full_name = fullName.trim(); payload.email = email.trim(); payload.mobile = mobile.trim(); if (preferred) payload.preferred_contact = preferred; }
    else if (mobile.trim()) { payload.mobile = mobile.trim(); }

    if (!authed && (!payload.full_name || !payload.email)) { toast.error("Please enter your name and email"); return; }
    if (!authed && !mobile.trim()) { toast.error("Please enter your mobile number with country code (e.g. +63…)"); return; }
    if (viewing && !date) { toast.error("Please choose a preferred date"); return; }
    if (availability) {
      if (!checkIn || !checkOut) { toast.error("Please choose check-in and check-out dates"); return; }
      if (checkOut <= checkIn) { toast.error("Check-out must be after check-in"); return; }
      if (parseInt(guests || 0) < 1) { toast.error("At least 1 guest is required"); return; }
      if (maxGuests > 0 && parseInt(guests || 0) > maxGuests) { toast.error(`This unit hosts up to ${maxGuests} guest(s)`); return; }
    }
    if (!viewing && !availability && !payload.message) { toast.error("Please enter a message"); return; }

    setBusy(true);
    try {
      await api.post(endpoint || `/public/listings/${listingId}/inquiries`, payload);
      toast.success(endpoint ? "Inquiry sent to the organizer" : availability ? "Availability request sent — the host will confirm" : viewing ? "Viewing request sent to the professional" : "Inquiry sent to the professional");
      reset();
      onOpenChange(false);
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  const title = heading || (availability ? "Check availability" : viewing ? "Request a viewing" : "Send an inquiry");
  const testid = availability ? "availability-dialog" : viewing ? "viewing-dialog" : "inquiry-dialog";
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md" data-testid={testid}>
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>
            {availability
              ? "Availability must be confirmed by the host or property manager — this is a request, not a booking."
              : viewing
              ? "Tell the representing professional when you'd like to view this property."
              : "Your message goes directly to the representing professional."}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-1">
          {!viewing && !availability && (
            <div>
              <Label className="text-xs font-semibold text-slate-500">Inquiry type</Label>
              <Select value={type} onValueChange={setType}>
                <SelectTrigger data-testid="inquiry-type-select" className="mt-1.5 h-11"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {GENERAL_TYPES.map((t) => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          )}

          {!authed && (
            <>
              <div>
                <Label htmlFor="inq-name" className="text-xs font-semibold text-slate-500">Full name</Label>
                <Input id="inq-name" data-testid="inquiry-name" value={fullName} onChange={(e) => setFullName(e.target.value)} placeholder="Your name" className="mt-1.5 h-11" />
              </div>
              <div>
                <Label htmlFor="inq-email" className="text-xs font-semibold text-slate-500">Email</Label>
                <Input id="inq-email" data-testid="inquiry-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@example.com" className="mt-1.5 h-11" />
              </div>
              <div>
                <Label htmlFor="inq-mobile" className="text-xs font-semibold text-slate-500">Mobile / WhatsApp / Viber Number*</Label>
                <Input id="inq-mobile" data-testid="inquiry-mobile" value={mobile} onChange={(e) => setMobile(e.target.value)} placeholder="+63 9xx xxx xxxx" className="mt-1.5 h-11" />
                <p className="text-[11px] text-slate-400 mt-1">Include your country code. This number may be used for calls, WhatsApp or Viber.</p>
              </div>
              <div>
                <Label className="text-xs font-semibold text-slate-500">Preferred contact method <span className="text-slate-400 font-normal">(optional)</span></Label>
                <Select value={preferred} onValueChange={setPreferred}>
                  <SelectTrigger data-testid="inquiry-preferred-contact" className="mt-1.5 h-11"><SelectValue placeholder="No preference" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="EMAIL">Email</SelectItem>
                    <SelectItem value="CALL">Call</SelectItem>
                    <SelectItem value="WHATSAPP">WhatsApp</SelectItem>
                    <SelectItem value="VIBER">Viber</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </>
          )}
          {authed && (
            <p className="text-xs text-slate-500" data-testid="inquiry-identity">Sending as <span className="font-semibold text-slate-700">{user.name || user.email}</span>{isConsumer ? "" : ""}</p>
          )}

          {viewing && (
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label htmlFor="inq-date" className="text-xs font-semibold text-slate-500">Preferred date</Label>
                <Input id="inq-date" data-testid="viewing-date" type="date" value={date} onChange={(e) => setDate(e.target.value)} className="mt-1.5 h-11" />
              </div>
              <div>
                <Label htmlFor="inq-time" className="text-xs font-semibold text-slate-500">Preferred time</Label>
                <Input id="inq-time" data-testid="viewing-time" type="time" value={time} onChange={(e) => setTime(e.target.value)} className="mt-1.5 h-11" />
              </div>
            </div>
          )}

          {availability && (
            <div className="grid grid-cols-3 gap-3">
              <div><Label className="text-xs font-semibold text-slate-500">Check-in</Label><Input data-testid="availability-checkin" type="date" value={checkIn} onChange={(e) => setCheckIn(e.target.value)} className="mt-1.5 h-11" /></div>
              <div><Label className="text-xs font-semibold text-slate-500">Check-out</Label><Input data-testid="availability-checkout" type="date" value={checkOut} onChange={(e) => setCheckOut(e.target.value)} className="mt-1.5 h-11" /></div>
              <div><Label className="text-xs font-semibold text-slate-500">Guests</Label><Input data-testid="availability-guests" type="number" min="1" value={guests} onChange={(e) => setGuests(e.target.value)} className="mt-1.5 h-11" /></div>
            </div>
          )}

          <div>
            <Label htmlFor="inq-msg" className="text-xs font-semibold text-slate-500">Message {(viewing || availability) && <span className="text-slate-400 font-normal">(optional)</span>}</Label>
            <Textarea id="inq-msg" data-testid="inquiry-message" value={message} onChange={(e) => setMessage(e.target.value)} rows={3}
              placeholder={viewing ? "Anything the professional should know?" : "I'm interested in this property…"} className="mt-1.5" />
          </div>
        </div>

        {!authed && (
          <p className="text-[11px] text-slate-500 leading-relaxed px-1" data-testid="inquiry-disclosure">
            By sending this inquiry, you agree to share your contact details with the representing professional so they can respond by email, phone, WhatsApp or Viber.
          </p>
        )}

        <DialogFooter>
          <Button data-testid="inquiry-submit" onClick={submit} disabled={busy} className="w-full h-11 rounded-xl bg-[#C79A3B] hover:bg-[#C79A3B]/90 text-white font-semibold">
            {busy ? "Sending…" : availability ? "Check availability" : viewing ? "Request viewing" : "Send inquiry"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
